import { useState, useContext, useEffect } from 'react'
import { PatrimonioContext } from '../../../contexts/patrimonioProvider'
import { ClientContext } from '../../../contexts/clientProvider'
import { UserContext } from '../../../contexts/userProvider'
import { FornecedoresContext } from '../../../contexts/fornecedoresProvider'
import DataTable from '../../Table'
import LoadingSpinner from '../../Loader/load'
import BaseSelect from '../../General/FormsComp/Select/BaseSelect'
import BaseButton from '../../General/FormsComp/Button/BaseButton'
import { BiChevronDown, BiX, BiFilter } from 'react-icons/bi'

const Estoque = () => {
    const { getAllPatrimoniosEstoque, patLoading } = useContext(PatrimonioContext)
    const { clientes } = useContext(ClientContext)
    const { setAlert } = useContext(UserContext)
    const { allMarcas, allModelos } = useContext(FornecedoresContext)
    const [filtros, setFiltros] = useState({})
    const [patrimoniosEstoque, setPatrimoniosEstoque] = useState([])
    const [loading, setLoading] = useState(false)
    const [marcas, setMarcas] = useState([])
    const [modelos, setModelos] = useState([])
    const [loadingOptions, setLoadingOptions] = useState(false)
    const [showFilters, setShowFilters] = useState(false)
    const [showSelections, setShowSelections] = useState(false)

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Para selects múltiplos, value pode ser um array
        setFiltros({ ...filtros, [name]: value });
    }

    // Carregamento das marcas e modelos
    const carregarMarcasModelos = async () => {
        setLoadingOptions(true)
        try {
            const [marcasData, modelosData] = await Promise.all([
                allMarcas(),
                allModelos()
            ])
            
            if (marcasData) {
                setMarcas([
                    { id: 'todos', nome: 'Todos' },
                    ...marcasData.map(marca => ({
                        id: marca.id,
                        nome: marca.nome
                    })).sort((a, b) => a.nome.localeCompare(b.nome))
                ])
            }
            
            if (modelosData) {
                setModelos([
                    { id: 'todos', nome: 'Todos' },
                    ...modelosData.map(modelo => ({
                        id: modelo.id,
                        nome: modelo.nome
                    })).sort((a, b) => a.nome.localeCompare(b.nome))
                ])
            }
        } catch (error) {
            console.error('Erro ao carregar marcas e modelos:', error)
        } finally {
            setLoadingOptions(false)
        }
    }

    // Carregamento inicial das opções
    useEffect(() => {
        carregarMarcasModelos()
    }, [])

    // Carregamento inicial de todos os patrimônios
    useEffect(() => {
        const carregarInicial = async () => {
            setLoading(true)
            try {
                const dados = await getAllPatrimoniosEstoque({})
                if (dados?.length > 0) {
                    const dadosFormatados = dados.map(item => ({
                        ...item,
                        created_at_formatted: item.created_at ? 
                            new Date(item.created_at).toLocaleDateString('pt-BR') : '-',
                        updated_at_formatted: item.updated_at ? 
                            new Date(item.updated_at).toLocaleDateString('pt-BR') : '-'
                    }))
                    setPatrimoniosEstoque(dadosFormatados)
                } else {
                    setPatrimoniosEstoque([])
                }
            } catch (error) {
                console.error('Erro ao carregar patrimônios em estoque:', error)
            } finally {
                setLoading(false)
            }
        }
        
        carregarInicial()
    }, [])

    // Busca com filtros aplicados
    const buscarPatrimoniosComFiltros = async () => {
        setLoading(true)
        try {
            // Preparar objeto de filtros para o backend (removendo "todos")
            const filtrosBackend = {
                id_clientes: filtros.id_cliente && Array.isArray(filtros.id_cliente) && filtros.id_cliente.length > 0 
                    ? filtros.id_cliente.filter(id => id !== 'todos') : undefined,
                id_marcas: filtros.id_marca && Array.isArray(filtros.id_marca) && filtros.id_marca.length > 0 
                    ? filtros.id_marca.filter(id => id !== 'todos') : undefined,
                id_modelos: filtros.id_modelo && Array.isArray(filtros.id_modelo) && filtros.id_modelo.length > 0 
                    ? filtros.id_modelo.filter(id => id !== 'todos') : undefined
            };
            
            // Se após filtrar "todos" a lista ficou vazia, não enviar esse filtro (retorna todos)
            if (filtrosBackend.id_clientes && filtrosBackend.id_clientes.length === 0) {
                filtrosBackend.id_clientes = undefined;
            }
            if (filtrosBackend.id_marcas && filtrosBackend.id_marcas.length === 0) {
                filtrosBackend.id_marcas = undefined;
            }
            if (filtrosBackend.id_modelos && filtrosBackend.id_modelos.length === 0) {
                filtrosBackend.id_modelos = undefined;
            }
            
            // Backend já retorna dados filtrados
            const dados = await getAllPatrimoniosEstoque(filtrosBackend)
            
            if (dados?.length > 0) {
                // Formatar dados para exibição
                const dadosFormatados = dados.map(item => ({
                    ...item,
                    created_at_formatted: item.created_at ? 
                        new Date(item.created_at).toLocaleDateString('pt-BR') : '-',
                    updated_at_formatted: item.updated_at ? 
                        new Date(item.updated_at).toLocaleDateString('pt-BR') : '-'
                }))
                
                setPatrimoniosEstoque(dadosFormatados)
            } else {
                setPatrimoniosEstoque([])
                
                // Só mostra alerta se há filtros aplicados E nenhum resultado
                const temFiltros = (filtrosBackend.id_clientes && filtrosBackend.id_clientes.length > 0) || 
                                 (filtrosBackend.id_marcas && filtrosBackend.id_marcas.length > 0) || 
                                 (filtrosBackend.id_modelos && filtrosBackend.id_modelos.length > 0)
                if (temFiltros) {
                    setAlert({ 
                        visible: true, 
                        title: "Informação", 
                        placeholder: 'Nenhum patrimônio em estoque encontrado com os filtros aplicados.', 
                        confirm: false, 
                        type: "warning" 
                    });
                }
            }
        } catch (error) {
            console.error('Erro ao buscar patrimônios em estoque:', error)
            setAlert({ 
                visible: true, 
                title: "Erro", 
                placeholder: 'Erro ao buscar patrimônios em estoque. Tente novamente.', 
                confirm: false, 
                type: "negative" 
            });
        } finally {
            setLoading(false)
        }
    }

    // Busca automática quando qualquer filtro é alterado
    useEffect(() => {
        // Sempre executa a busca, mesmo quando os filtros estão vazios
        buscarPatrimoniosComFiltros()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtros.id_cliente, filtros.id_marca, filtros.id_modelo])

    const clearFilters = () => {
        // Limpa os filtros e a busca será disparada automaticamente pelo useEffect
        setFiltros({})
    }

    // Remover um cliente específico
    const removeCliente = (clienteId) => {
        const novosClientes = (filtros.id_cliente || []).filter(id => id !== clienteId)
        setFiltros({ ...filtros, id_cliente: novosClientes.length > 0 ? novosClientes : undefined })
    }

    // Remover um modelo específico
    const removeModelo = (modeloId) => {
        const novosModelos = (filtros.id_modelo || []).filter(id => id !== modeloId)
        setFiltros({ ...filtros, id_modelo: novosModelos.length > 0 ? novosModelos : undefined })
    }

    // Remover todos os clientes
    const removeAllClientes = () => {
        setFiltros({ ...filtros, id_cliente: undefined })
    }

    // Remover todos os modelos
    const removeAllModelos = () => {
        setFiltros({ ...filtros, id_modelo: undefined })
    }

    // Contar filtros ativos (excluindo "todos")
    const countFiltrosAtivos = () => {
        let count = 0
        if (filtros.id_cliente && Array.isArray(filtros.id_cliente) && filtros.id_cliente.filter(id => id !== 'todos').length > 0) count++
        if (filtros.id_modelo && Array.isArray(filtros.id_modelo) && filtros.id_modelo.filter(id => id !== 'todos').length > 0) count++
        return count
    }

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showFilters && !event.target.closest('.filter-dropdown')) {
                setShowFilters(false)
            }
            if (showSelections && !event.target.closest('.selections-dropdown')) {
                setShowSelections(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showFilters, showSelections])

    // Headers da tabela
    const headers = [
        { 
            key: 'etiqueta', 
            label: 'Nº Etiqueta', 
            sortable: true,
            tooltip: 'Número da etiqueta do patrimônio'
        },
        { 
            key: 'serie', 
            label: 'Nº Série', 
            sortable: true,
            tooltip: 'Número de série do equipamento'
        },
        { 
            key: 'mac', 
            label: 'MAC', 
            sortable: false,
            tooltip: 'Endereço MAC do equipamento'
        },
        { 
            key: 'nome', 
            label: 'Nome', 
            sortable: true,
            tooltip: 'Tipo/modelo do equipamento'
        },
        { 
            key: 'nome_marca', 
            label: 'Marca', 
            sortable: true,
            tooltip: 'Marca do equipamento'
        },
        { 
            key: 'nome_modelo', 
            label: 'Modelo', 
            sortable: true,
            tooltip: 'Modelo do equipamento'
        },        
        {
            key: 'nome_cliente',
            label: 'Cliente',
            sortable: true,
            wrapText: true,
            tooltip: 'Cliente do equipamento'
        }
    
    ]

    // Preparar opções de clientes para o select
    const clientesOptions = clientes ? 
        [
            { id: 'todos', nome: 'Todos' },
            ...clientes.map(cliente => ({
                id: cliente.id,
                nome: cliente.nome
            })).sort((a, b) => a.nome.localeCompare(b.nome))
        ] : []

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-700 rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-orange-900 dark:text-orange-100">Patrimônios em Estoque</h1>
                        <p className="text-orange-700 dark:text-orange-300">Consulte equipamentos disponíveis em estoque</p>
                    </div>
                </div>

                {/* Filtros Avançados */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        {/* Botão Filtros Avançados */}
                        <div className="relative filter-dropdown">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                data-tour-id="estoque:filtros"
                                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-vox-blue-600 to-vox-blue-700 hover:from-vox-blue-700 hover:to-vox-blue-800 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-vox-blue-500 focus:ring-offset-2"
                            >
                                <BiFilter className="w-5 h-5" />
                                Filtros Avançados
                                {countFiltrosAtivos() > 0 && (
                                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                                        {countFiltrosAtivos()}
                                    </span>
                                )}
                                <BiChevronDown className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown de Filtros */}
                            {showFilters && (
                                <div data-tour-id="estoque:painel-filtros" className="absolute left-0 top-full mt-2 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl z-50 max-h-[500px] overflow-y-auto">
                                    <div className="p-4 space-y-4">
                                        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros Aplicados</h3>
                                            <button
                                                onClick={() => setShowFilters(false)}
                                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                            >
                                                <BiX className="w-5 h-5" />
                                            </button>
                                        </div>

                                        {/* Cliente */}
                                        <div>
                                            <BaseSelect
                                                label="Cliente (Opcional)"
                                                name="id_cliente"
                                                value={filtros.id_cliente || []}
                                                onChange={handleInputChange}
                                                options={clientesOptions}
                                                placeholder="Selecione um ou mais clientes"
                                                multiple={true}
                                            />
                                        </div>

                                        {/* Modelo */}
                                        <div>
                                            <BaseSelect
                                                label="Modelo (Opcional)"
                                                name="id_modelo"
                                                value={filtros.id_modelo || []}
                                                onChange={handleInputChange}
                                                options={modelos}
                                                placeholder="Selecione um ou mais modelos"
                                                disabled={loadingOptions}
                                                multiple={true}
                                            />
                                        </div>

                                        {/* Botões do Dropdown */}
                                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                            <button
                                                onClick={clearFilters}
                                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors duration-200"
                                            >
                                                Limpar
                                            </button>
                                            <button
                                                onClick={() => setShowFilters(false)}
                                                data-tour-id="estoque:aplicar"
                                                className="px-4 py-2 bg-gradient-to-r from-vox-green-600 to-vox-green-700 hover:from-vox-green-700 hover:to-vox-green-800 text-white font-medium rounded-lg transition-all duration-200"
                                            >
                                                Aplicar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Seleções Ativas */}
                        {countFiltrosAtivos() > 0 && (
                            <div className="relative selections-dropdown flex-1">
                                <button
                                    onClick={() => setShowSelections(!showSelections)}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-vox-green-50 dark:bg-vox-green-900/20 border border-vox-green-200 dark:border-vox-green-700 text-vox-green-700 dark:text-vox-green-300 rounded-lg hover:bg-vox-green-100 dark:hover:bg-vox-green-900/30 transition-colors duration-200"
                                >
                                    <span className="font-medium">Seleções Ativas ({countFiltrosAtivos()})</span>
                                    <BiChevronDown className={`w-4 h-4 transition-transform ${showSelections ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown de Seleções */}
                                {showSelections && (
                                    <div className="absolute left-0 top-full mt-2 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl z-50 max-h-[400px] overflow-y-auto">
                                        <div className="p-4 space-y-4">
                                            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3">
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros Aplicados</h3>
                                                <button
                                                    onClick={clearFilters}
                                                    className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                                >
                                                    Limpar tudo
                                                </button>
                                            </div>

                                            {/* Clientes Selecionados */}
                                            {filtros.id_cliente && filtros.id_cliente.length > 0 && (
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Clientes ({filtros.id_cliente.filter(id => id !== 'todos').length})
                                                        </span>
                                                        <button
                                                            onClick={removeAllClientes}
                                                            className="text-xs text-red-600 hover:text-red-700"
                                                        >
                                                            Remover todos
                                                        </button>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {filtros.id_cliente
                                                            .filter(id => id !== 'todos')
                                                            .map(clienteId => {
                                                                const cliente = clientesOptions.find(c => String(c.id) === String(clienteId))
                                                                return cliente ? (
                                                                    <span
                                                                        key={clienteId}
                                                                        className="inline-flex items-center gap-1 px-2 py-1 bg-vox-blue-100 dark:bg-vox-blue-900/30 text-vox-blue-800 dark:text-vox-blue-200 rounded text-sm"
                                                                    >
                                                                        {cliente.nome}
                                                                        <button
                                                                            onClick={() => removeCliente(clienteId)}
                                                                            className="hover:bg-vox-blue-200 dark:hover:bg-vox-blue-800 rounded-full p-0.5"
                                                                        >
                                                                            <BiX className="w-3 h-3" />
                                                                        </button>
                                                                    </span>
                                                                ) : null
                                                            })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Modelos Selecionados */}
                                            {filtros.id_modelo && filtros.id_modelo.length > 0 && (
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Modelos ({filtros.id_modelo.filter(id => id !== 'todos').length})
                                                        </span>
                                                        <button
                                                            onClick={removeAllModelos}
                                                            className="text-xs text-red-600 hover:text-red-700"
                                                        >
                                                            Remover todos
                                                        </button>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {filtros.id_modelo
                                                            .filter(id => id !== 'todos')
                                                            .map(modeloId => {
                                                                const modelo = modelos.find(m => String(m.id) === String(modeloId))
                                                                return modelo ? (
                                                                    <span
                                                                        key={modeloId}
                                                                        className="inline-flex items-center gap-1 px-2 py-1 bg-vox-green-100 dark:bg-vox-green-900/30 text-vox-green-800 dark:text-vox-green-200 rounded text-sm"
                                                                    >
                                                                        {modelo.nome}
                                                                        <button
                                                                            onClick={() => removeModelo(modeloId)}
                                                                            className="hover:bg-vox-green-200 dark:hover:bg-vox-green-800 rounded-full p-0.5"
                                                                        >
                                                                            <BiX className="w-3 h-3" />
                                                                        </button>
                                                                    </span>
                                                                ) : null
                                                            })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex justify-center items-center py-8">
                    <LoadingSpinner />
                </div>
            )}

            {/* Tabela de Resultados */}
            {patrimoniosEstoque.length > 0 && !loading && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Resultados da Consulta
                            </h2>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {patrimoniosEstoque.length} item(ns) encontrado(s)
                                </span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                                    ESTOQUE
                                </span>
                            </div>
                        </div>
                    </div>
                    <DataTable
                        data={patrimoniosEstoque}
                        headers={headers}
                        docTitle="Patrimônios em Estoque"
                        exportButton={true}
                    />
                </div>
            )}

            {/* Estado vazio */}
            {!loading && patrimoniosEstoque.length === 0 && Object.keys(filtros).length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Nenhum resultado encontrado</h3>
                    <p className="text-gray-600 dark:text-gray-400">Não foram encontrados patrimônios em estoque com os filtros aplicados.</p>
                </div>
            )}
        </div>
    )
}

export default Estoque
